import { Tabs, TabsProps } from 'antd';
import React, { useEffect, useMemo } from 'react';

import Icon from '@ant-design/icons';

import { EEventManagementTabsType, EMetricsManagementTabsType } from '@src/common/constant/enum';
import DeployButton from '@src/pages/main/components/DeployButton';
import { trackManagementTabsClick } from '@src/sensor';
import MetricsSvg from '@icons/metrics/metrics.svg';
import FlowsSvg from '@icons/home/home-flow.svg';
import MappedEventSvg from '@icons/emp/mapping-builder-lined.svg';
import CustomDimensionSvg from '@icons/home/home-dimensions.svg';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  DimensionManagement,
  MappedEventsManagement,
  useMetricsSubTabStore,
} from '@src/emp-3.0/components/Management';
import Flow from '@src/emp-3.0/components/Management/Metrics/Flow';
import IndividualMetric from '@src/emp-3.0/components/Management/Metrics/IndividualMetric';
import { managementRouteMap } from '@src/pages/management/constant';

import styles from './index.less';
import BadEventManagement from '@src/emp-3.0/components/Management/BadEvents';
import { useShowKpiSetting, useInternalAdmin } from '@hooks';
import { WorkbenchDeployButtons } from '@src/pages/main/components/DeployButton/workbench';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';
import ExportAsset from '@src/emp-3.0/components/Management/components/Export/ExportAsset';

export const Management = () => {
  const { workbenchMode } = useInitialInfoStore();
  const location = useLocation();
  const navigate = useNavigate();

  const internalAdmin = useInternalAdmin();
  const showKpiSetting = useShowKpiSetting();

  const { pathname } = location;
  const activeKey = pathname;

  const { setActiveTabKey } = useMetricsSubTabStore((state) => state);
  useEffect(() => {
    if (pathname === managementRouteMap[EEventManagementTabsType.KPI_SETTING] && !showKpiSetting) {
      navigate('/home', { replace: true });
    }
  }, [pathname, showKpiSetting]);

  const items: TabsProps['items'] = useMemo(() => {
    const items: any[] = [
      {
        key: managementRouteMap[EEventManagementTabsType.METRICS],
        label: (
          <div>
            <Icon component={MetricsSvg} style={{ marginRight: '6px' }} />
            <span>Metrics</span>
          </div>
        ),
      },
      {
        key: managementRouteMap[EEventManagementTabsType.FLOWS],
        label: (
          <div>
            <Icon component={FlowsSvg} style={{ marginRight: '6px' }} />
            <span>Flows</span>
          </div>
        ),
      },
      {
        key: managementRouteMap[EEventManagementTabsType.CUSTOM_DIMENSION],
        label: (
          <div>
            <Icon component={CustomDimensionSvg} style={{ marginRight: '6px' }} />
            <span>Custom Dimension</span>
          </div>
        ),
      },
      {
        key: managementRouteMap[EEventManagementTabsType.MAPPED_EVENT],
        label: (
          <div>
            <Icon component={MappedEventSvg} style={{ marginRight: '6px' }} />
            <span>Mapped Events</span>
          </div>
        ),
      },
      showKpiSetting && {
        key: managementRouteMap[EEventManagementTabsType.KPI_SETTING],
        label: `KPI Setting`,
      },
    ].filter(Boolean);
    return items;
  }, [internalAdmin, showKpiSetting]);

  const getTabBarExtraContent = (activeTab) => {
    if (activeTab === managementRouteMap[EEventManagementTabsType.MAPPED_EVENT]) {
      return {};
    }
    return null;
  };

  return (
    <div className={styles.rootContainer}>
      <div className={styles.titlePanel}>
        <span>Management</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ExportAsset />
          {workbenchMode ? <WorkbenchDeployButtons /> : <DeployButton />}
        </div>
      </div>
      <div className={styles.contentContainer}>
        <Tabs
          tabBarStyle={{ padding: '0 16px' }}
          activeKey={activeKey}
          items={items}
          tabBarExtraContent={getTabBarExtraContent(activeKey)}
          onChange={(activeKey) => {
            trackManagementTabsClick({ activeKey });
            navigate(activeKey);
          }}
        />

        <div>
          <Routes>
            <Route
              path={`/`}
              element={
                <Navigate to={managementRouteMap[EEventManagementTabsType.METRICS]} replace />
              }
            />
            <Route path={`/metrics/*`} element={<IndividualMetric />} />
            <Route path={`/flows/*`} element={<Flow />} />
            <Route path={`/custom-dimension`} element={<DimensionManagement />} />
            <Route path={`/mapped-events`} element={<MappedEventsManagement />} />
            {showKpiSetting && <Route path={`/bad-events`} element={<BadEventManagement />} />}
          </Routes>
        </div>
      </div>
    </div>
  );
};
